import {
	getCoreRowModel,
	getSortedRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { useTranslate } from "@tolgee/react";
import { Download } from "lucide-react";
import { useCallback } from "react";
import type { UserFill } from "#/hooks/use-hyperliquid-account";
import { TRADES_GRID_CLASS } from "./constants/positions.constants.ts";
import { downloadCsv } from "./lib/download-csv.ts";
import { tradesColumns } from "./trades-columns.tsx";
import { VirtualizedGridTable } from "./virtualized-grid-table.tsx";

const coreRowModel = getCoreRowModel();
const sortedRowModel = getSortedRowModel();

type TradesTableProps = {
	fills: UserFill[];
	fetchNextPage: () => void;
	hasNextPage: boolean;
	isFetchingNextPage: boolean;
	isError?: boolean;
	onRetry?: () => void;
};

export function TradesTable({
	fills,
	fetchNextPage,
	hasNextPage,
	isFetchingNextPage,
	isError,
	onRetry,
}: TradesTableProps) {
	const { t } = useTranslate();

	const table = useReactTable({
		data: fills,
		columns: tradesColumns,
		getCoreRowModel: coreRowModel,
		getSortedRowModel: sortedRowModel,
	});

	const handleExportCsv = useCallback(() => {
		if (fills.length === 0) return;
		const headers = [
			"Time",
			"Market",
			"Side",
			"Price",
			"Size",
			"Fee",
			"Realized PnL",
		];
		const rows = fills.map((f) => [
			new Date(f.time).toISOString(),
			`${f.coin}-PERP`,
			f.side === "B" ? "Buy" : "Sell",
			f.px,
			`${f.sz} ${f.coin}`,
			f.fee,
			f.closedPnl,
		]);
		downloadCsv(
			`trade-history-${new Date().toISOString().slice(0, 10)}.csv`,
			headers,
			rows,
		);
	}, [fills]);

	return (
		<div className="flex min-h-0 flex-1 flex-col">
			<VirtualizedGridTable
				table={table}
				gridClass={TRADES_GRID_CLASS}
				estimateSize={36}
				emptyMessage={t("positions.no-trades")}
				onScrollNearEnd={hasNextPage ? fetchNextPage : undefined}
				isLoadingMore={isFetchingNextPage}
				isError={isError}
				onRetry={onRetry}
			/>
			{fills.length > 0 && (
				<div className="flex shrink-0 items-center border-t border-trade-border-subtle px-4 py-1">
					<button
						type="button"
						onClick={handleExportCsv}
						className="flex items-center gap-1.5 text-caption1 text-trade-text-secondary hover:text-trade-text transition-colors cursor-pointer"
					>
						<Download className="size-3" />
						{t("export-csv")}
					</button>
				</div>
			)}
		</div>
	);
}
